import React from "react";
import styled from "@emotion/styled";
import styleVariables from "../../helpers/styleVariables";
import { useTranslation } from "next-i18next";
import Button, { ButtonVariant } from "../button/button";
import Select from "../select/select";
import Container from "@mui/material/Container";
import { Grid } from "@mui/material";
import { dehydrate, QueryClient, useQuery } from "react-query";
import queryKeys from "../../api/queryKeys";
import getLeaderboardsFilters from "../../api/queries/getLeaderboardsFilters";
import { useRouter } from "next/router";
import { FilterGetParams } from "../../../pages/leaderboards";
import getProfile from "../../api/queries/getProfile";
import Heading2 from "../typo/heading2/heading2";
import Heading1 from "../typo/heading1/heading1";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/system";

const Root = styled.div`
  background: ${styleVariables.colors.beige};
  padding-top: 32px;
  padding-bottom: 32px;
`;

const MobileScrollAnchor = styled.div`
  display: block;
  position: relative;
  top: -65px;
`;

interface FiltersProps {
  activeDiscipline?: string;
  activeCategory?: string;
  activeCountry?: string;
  activeClub?: string;
  activeDisplay?: string;
  query: FilterGetParams;
  title: string;
}

const getObjectWithoutUndefinedValues = (input: Object) => {
  return JSON.parse(JSON.stringify(input));
};

const Filters: React.FC<FiltersProps> = ({ query, title }) => {
  const router = useRouter();
  const scrollAnchorRef = React.useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [filtersMobileOpen, setFiltersMobileOpen] =
    React.useState<boolean>(false);

  const { data: leaderboardsFiltersData } = useQuery(
    queryKeys.leaderboardsFilters(),
    getLeaderboardsFilters
  );

  const shooterId = query.id ? parseInt(query.id) : undefined;

  const { data: shooterData } = useQuery(
    shooterId ? queryKeys.profile(shooterId) : "",
    () => (shooterId ? getProfile(shooterId) : undefined),
    { enabled: Boolean(shooterId) }
  );

  const { t } = useTranslation();

  const [queryToSubmit, setQueryToSubmit] =
    React.useState<FilterGetParams>(query);

  const handleSelectChange = (key: string, input: string) => {
    setQueryToSubmit((prev) => {
      return {
        ...prev,
        [key]: input || undefined,
      };
    });
  };

  const handleSubmitFilter = () => {
    router.push(
      { query: getObjectWithoutUndefinedValues(queryToSubmit) },
      undefined,
      { scroll: false }
    );
    if (!isMdUp) {
      setFiltersMobileOpen(false);
    }
  };

  React.useEffect(() => {
    if (filtersMobileOpen === false) {
      if (scrollAnchorRef && scrollAnchorRef.current) {
        scrollAnchorRef.current.scrollIntoView();
      }
    }
  }, [filtersMobileOpen]);

  React.useEffect(() => {
    setQueryToSubmit(query);
  }, [query]);

  if (!leaderboardsFiltersData) {
    return null;
  }

  const {
    discipline: disciplineOptions,
    category: categoryOptions,
    country: countryOptions,
    club: clubOptions,
  } = leaderboardsFiltersData;

  const sortKeys = [
    {
      key: "discipline",
      priority: 1,
    },
    {
      key: "category",
      priority: 2,
    },
    {
      key: "country",
      priority: 3,
    },
    {
      key: "club",
      priority: 4,
    },
  ];

  const queryEntriesSortedByPriority = Object.entries(query).sort(function (
    a: any,
    b: any
  ) {
    const aToCompare = sortKeys.find(({ key }) => key === a[0])?.priority;
    const bToCompare = sortKeys.find(({ key }) => key === b[0])?.priority;
    if (aToCompare && bToCompare) {
      if (aToCompare > bToCompare) {
        return 1;
      }
      if (bToCompare > aToCompare) {
        return -1;
      }
    }

    return 0;
  });

  const headingOneData = queryEntriesSortedByPriority
    .map((item) => {
      const key = item[0];
      const value = item[1];
      // @ts-ignore
      return leaderboardsFiltersData[key]?.find(
        ({ id }: { id: string | number }) => id.toString() === value
      ).name;
    })
    .filter((item) => item !== undefined);

  const canShowFilters = (!isMdUp && filtersMobileOpen) || isMdUp;

  return (
    <>
      <Root>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Heading2>{title}</Heading2>
            </Grid>
            {canShowFilters && (
              <>
                <Grid item xs={12} md={2}>
                  <Select
                    label={t("filter.discipline")}
                    options={disciplineOptions.filter(({ id }) =>
                      !shooterData
                        ? true
                        : shooterData.discipline.includes(id.toString())
                    )}
                    value={queryToSubmit.discipline}
                    handleChange={(input: string) =>
                      handleSelectChange("discipline", input)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    displayEmpty
                    label={t("filter.category")}
                    options={categoryOptions.filter(({ id }) =>
                      !shooterData ? true : id === shooterData.category
                    )}
                    value={queryToSubmit.category}
                    handleChange={(input: string) =>
                      handleSelectChange("category", input)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    displayEmpty
                    label={t("filter.country")}
                    options={countryOptions
                      .filter(({ id }) =>
                        !shooterData ? true : id === shooterData.country.id
                      )
                      .map(({ id, name }) => ({
                        id: id.toString(),
                        name,
                      }))}
                    value={queryToSubmit.country}
                    handleChange={(input: string) =>
                      handleSelectChange("country", input)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    displayEmpty
                    label={t("filter.club")}
                    options={clubOptions
                      .filter(({ id }) =>
                        !shooterData ? true : id === shooterData.club?.id
                      )
                      .map(({ id, name }) => ({
                        id: id.toString(),
                        name,
                      }))}
                    value={queryToSubmit.club}
                    handleChange={(input: string) =>
                      handleSelectChange("club", input)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Select
                    label={t("filter.display")}
                    options={[
                      { name: t("filter.totalScore"), id: "totalScore" },
                      { name: t("filter.averageScore"), id: "averageScore" },
                    ]}
                    value={queryToSubmit.display}
                    handleChange={(input: string) =>
                      handleSelectChange("display", input)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box display="flex" height="100%" alignItems="flex-end">
                    <Button
                      className="mt-2 w-100"
                      handleClick={handleSubmitFilter}
                      variant={ButtonVariant.primary}
                    >
                      {t("filter.show")}
                    </Button>
                  </Box>
                </Grid>
              </>
            )}
            {!isMdUp && !filtersMobileOpen && (
              <Grid item xs={12} md={2}>
                <Button
                  className="mt-2 w-100"
                  handleClick={() => setFiltersMobileOpen(true)}
                  variant={ButtonVariant.primary}
                >
                  {t("filter.filters")}
                </Button>
              </Grid>
            )}
          </Grid>
        </Container>
      </Root>
      <MobileScrollAnchor ref={scrollAnchorRef} />
      <Container>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box mt={4} />
            <Heading1>{headingOneData.join(" / ")}</Heading1>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export async function getServerSideProps() {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery(
    queryKeys.leaderboardsFilters(),
    getLeaderboardsFilters
  );
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}

export default Filters;
